import { Controller } from "@hotwired/stimulus";

/**
 * Opens a club's film and photographs full screen, as MediaViewer does.
 *
 * The dialog's markup is already on the page; this opens it at the item that was clicked, keeps the
 * counter in step with the page the reader is on, and lets a pointer drag page it the way a finger
 * already can. Escape, the inert background and the focus trap come from `<dialog>` itself.
 *
 * `showModal()` rather than `show()`: only the modal form gets the top layer, the `::backdrop` and
 * the Escape handling, and a non-modal dialog would leave the page behind it scrollable under the
 * photograph.
 *
 * Stopping the film is bound to the dialog's own `close` event rather than to the close button,
 * because Escape closes a `<dialog>` without going through any handler of ours - a film carried on
 * playing with sound behind a closed viewer, which is the kind of thing nobody reports and
 * everybody notices.
 */
export default class extends Controller<HTMLElement> {
  static override targets = ["counter", "dialog", "item", "track"];

  declare readonly counterTarget: HTMLElement;
  declare readonly dialogTarget: HTMLDialogElement;
  declare readonly hasCounterTarget: boolean;
  declare readonly itemTargets: HTMLElement[];
  declare readonly trackTarget: HTMLElement;

  /** Where a pointer drag started, or undefined when none is in progress. */
  private origin: { scrollLeft: number; x: number } | undefined;

  override disconnect(): void {
    this.stopped();
  }

  open(event: Event): void {
    event.preventDefault();

    const trigger = event.currentTarget as HTMLElement;
    const index = Number(trigger.dataset["mediaViewerIndexParam"] ?? "0");

    this.dialogTarget.showModal();

    /* After the dialog is shown, so the track has a width to scroll within: a scrollLeft set on
           a `display: none` element is silently dropped and the viewer opens on the first item
           however far down the strip the reader clicked. */
    this.page(index, "instant");
    this.render(index);
  }

  close(): void {
    this.dialogTarget.close();
  }

  /** The dialog's own `close` event, so Escape and the button are the same path. */
  stopped(): void {
    this.element.querySelectorAll("video").forEach((video) => video.pause());
  }

  /** Keeps the counter honest while the reader pages, bound to `scroll` on the track. */
  track(): void {
    this.render(this.current());
  }

  /** Left and right page, as the arrows on a gallery are expected to. */
  key(event: KeyboardEvent): void {
    const step =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (step === 0) {
      return;
    }

    event.preventDefault();
    this.page(this.clamp(this.current() + step), "smooth");
  }

  /**
   * A drag on a pointer that cannot already swipe.
   *
   * Touch is left alone: the track scrolls natively there, and taking the gesture over would
   * replace momentum and rubber-banding with a worse copy of both. `setPointerCapture` keeps the
   * drag alive when the cursor leaves the window, and snapping is turned off for its duration
   * because a mandatory snap fights a scrollLeft written on every move.
   */
  dragStart(event: PointerEvent): void {
    if (event.pointerType === "touch") {
      return;
    }

    /* A pointer that came down on the film is the film's own. Taking it would `preventDefault`
           the control's default action and then `setPointerCapture` the pointerup away from it, so
           the button never completes -- which is why play, the scrubber and the volume were all
           dead in Safari. Chrome's controls synthesise a click of their own and survived it, which
           is what made this look like a codec problem rather than a handler one. */
    if ((event.target as HTMLElement).closest("video") !== null) {
      return;
    }

    event.preventDefault();
    this.origin = { scrollLeft: this.trackTarget.scrollLeft, x: event.clientX };
    this.trackTarget.style.scrollSnapType = "none";
    this.trackTarget.setPointerCapture(event.pointerId);
  }

  dragMove(event: PointerEvent): void {
    if (this.origin === undefined) {
      return;
    }

    this.trackTarget.scrollLeft =
      this.origin.scrollLeft - (event.clientX - this.origin.x);
  }

  dragEnd(): void {
    if (this.origin === undefined) {
      return;
    }

    this.origin = undefined;
    this.trackTarget.style.scrollSnapType = "";
    this.page(this.current(), "smooth");
  }

  private current(): number {
    const width = this.trackTarget.clientWidth;

    return width === 0
      ? 0
      : this.clamp(Math.round(this.trackTarget.scrollLeft / width));
  }

  private clamp(index: number): number {
    return Math.min(
      Math.max(index, 0),
      Math.max(this.itemTargets.length - 1, 0),
    );
  }

  private page(index: number, behavior: ScrollBehavior): void {
    this.trackTarget.scrollTo({
      left: index * this.trackTarget.clientWidth,
      behavior,
    });
    this.render(index);
  }

  private render(index: number): void {
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${index + 1} / ${this.itemTargets.length}`;
    }
  }
}
